import {alpha2ToAlpha3LanguageCode} from '@/common/constants/language';
import {LocationType} from '@/common/constants/locationType';
import {YearPeriod} from '@/common/interfaces/date';
import {Coordinates} from '@/common/interfaces/geolocation';
import {generateLocationAttributeMask, LocationField} from '@/common/helpers/location/generateLocationAttributeMask';
import {AbstractFetcher} from '@/common/services/fetcher/AbstractFetcher';
import {parseLocationPoint} from '../helpers/locationHelpers';

export const getByIds =
	(fetcher: AbstractFetcher) =>
	async (locations: string[], lang: string, fields: LocationField[] = []) => {
		const attributes = generateLocationAttributeMask(fields);
		const response = await fetcher.fetch<{locations: any[]}>('/api/location/list/by_id', {
			locations,
			lang: alpha2ToAlpha3LanguageCode[lang],
			attributes,
		});

		return response.locations.map(parseLocationPoint);
	};

export const determine = (fetcher: AbstractFetcher) => async (coordinates: Coordinates | undefined, lang: string) => {
	const response = await fetcher.fetch<{location: any; superior: any}>('/api/location/determine', {
		coordinates,
		lang: alpha2ToAlpha3LanguageCode[lang],
	});

	return {
		location: parseLocationPoint(response.location),
	};
};

export const getLargestSettlements =
	(fetcher: AbstractFetcher) =>
	async (locationId: string, lang: string, limit: number, fields: LocationField[] = []) => {
		const attributes = generateLocationAttributeMask(fields);
		const response = await fetcher.fetch<{locations: any[]}>('/api/location/settle/list/largest', {
			/* eslint-disable @typescript-eslint/naming-convention */
			location_id: locationId,
			lang: alpha2ToAlpha3LanguageCode[lang],
			limit,
			attributes,
			/* eslint-enable @typescript-eslint/naming-convention */
		});

		return response.locations.map(parseLocationPoint);
	};

export const getSubordinateLocations =
	(fetcher: AbstractFetcher) =>
	async (locationId: string, lang: string, limit: number, locationType?: LocationType, offsetLocationId?: string) => {
		const response = await fetcher.fetch<{locations: any[]}>('/api/location/list/subordinates', {
			/* eslint-disable @typescript-eslint/naming-convention */
			location_id: locationId,
			lang: alpha2ToAlpha3LanguageCode[lang],
			limit,
			location_type: locationType,
			offset_location_id: offsetLocationId,
			/* eslint-enable @typescript-eslint/naming-convention */
		});

		return response.locations.map(parseLocationPoint);
	};

export const getContinentLocations = (fetcher: AbstractFetcher) => async (lang: string) => {
	const response = await fetcher.fetch<{locations: any[]}>('/api/location/continent/list/all', {
		lang: alpha2ToAlpha3LanguageCode[lang],
	});

	return response.locations.map(parseLocationPoint);
};

export const getResortLocations =
	(fetcher: AbstractFetcher) => async (countryId: string, lang: string, limit: number, type?: YearPeriod) => {
		const response = await fetcher.fetch<{locations: any[]}>('/api/location/resort/list/by_country', {
			/* eslint-disable @typescript-eslint/naming-convention */
			country_id: countryId,
			lang: alpha2ToAlpha3LanguageCode[lang],
			limit,
			type,
			/* eslint-enable @typescript-eslint/naming-convention */
		});

		return response.locations.map(parseLocationPoint);
	};

export const getRecentLocations = (fetcher: AbstractFetcher) => async (lang: string, limit?: number) => {
	const response = await fetcher.fetch<{locations: any[]}>('/api/location/recent/list/all', {
		lang: alpha2ToAlpha3LanguageCode[lang],
		limit,
	});
	return response.locations.map(parseLocationPoint);
};

export const addRecentLocation = (fetcher: AbstractFetcher) => async (locationId: string) => {
	await fetcher.fetch('/api/location/recent/add', {
		/* eslint-disable @typescript-eslint/naming-convention */
		location_id: locationId,
		/* eslint-enable @typescript-eslint/naming-convention */
	});
};
